import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import type { BundleMetadata } from "@/components/bundle/bundleTypes";
import { backendClient } from "@/lib/api-client";
import { uploadFileWithRetry } from "@/lib/fileUploadUtils";

type UseTitlePageOptions = {
  settings: BundleMetadata;
  onChange: (metadata: BundleMetadata) => void;
  onPendingChange: (hasPending: boolean) => void;
  matterSlug: string;
  bundleId: string;
  /** Server bundle timestamp. Changes when the stored title page may have been regenerated. */
  serverUpdatedAt?: string | null;
};

export function useTitlePage({
  settings,
  onChange,
  onPendingChange,
  matterSlug,
  bundleId,
  serverUpdatedAt,
}: UseTitlePageOptions) {
  const [resetting, setResetting] = useState(false);
  const [serverBuffer, setServerBuffer] = useState<ArrayBuffer | null>(null);
  const [serverLoading, setServerLoading] = useState(false);
  const [pendingBuffer, setPendingBuffer] = useState<ArrayBuffer | null>(null);
  const [pendingFormat, setPendingFormat] = useState<'pdf' | 'docx' | null>(null);
  const hasPending = pendingBuffer !== null;

  useEffect(() => {
    onPendingChange(hasPending);
  }, [hasPending, onPendingChange]);

  // pendingFormat is always set when pendingBuffer is set; settings.title_page.format is non-nullable
  const format: 'pdf' | 'docx' = hasPending ? pendingFormat! : settings.title_page.format;
  const previewBuffer = hasPending ? pendingBuffer : serverBuffer;
  const previewLoading = !hasPending && serverLoading;

  // A new server revision (or another bundle) invalidates a staged upload.
  // Do not do this in the fetch effect: that also runs when format/hasPending
  // change and would wipe an in-progress upload.
  useEffect(() => {
    setPendingBuffer(null);
    setPendingFormat(null);
  }, [serverUpdatedAt, bundleId]);

  useEffect(() => {
    if (!settings.title_page.enabled || !settings.title_page.format || hasPending) {
      if (!hasPending) setServerBuffer(null);
      return;
    }

    let cancelled = false;
    setServerLoading(true);

    backendClient
      .get(`/bundle/matter/${matterSlug}/${bundleId}/title-page`, { responseType: "arraybuffer" })
      .then((res) => { if (!cancelled) setServerBuffer(res.data); })
      .catch(() => { if (!cancelled) setServerBuffer(null); })
      .finally(() => { if (!cancelled) setServerLoading(false); });

    return () => { cancelled = true; };
  }, [
    settings.title_page.enabled,
    settings.title_page.format,
    matterSlug,
    bundleId,
    hasPending,
    serverUpdatedAt,
  ]);

  const handleUpload = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'pdf' && ext !== 'docx') {
      toast.error('Only PDF and DOCX files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10MB limit');
      return;
    }

    file.arrayBuffer().then((buffer) => {
      const fmt = ext === 'docx' ? 'docx' : 'pdf';
      setPendingBuffer(buffer);
      setPendingFormat(fmt);
      onChange({ ...settings, title_page: { ...settings.title_page, format: fmt, edited: true } });
    });
  }, [onChange, settings]);

  const handleReset = useCallback(async () => {
    setResetting(true);
    try {
      const res = await backendClient.get(
        `/bundle/matter/${matterSlug}/${bundleId}/title-page/default`,
        { responseType: "arraybuffer" },
      );
      setPendingBuffer(res.data);
      setPendingFormat('docx');
      onChange({ ...settings, title_page: { ...settings.title_page, format: 'docx', edited: false } });
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Error loading default title page');
    } finally {
      setResetting(false);
    }
  }, [matterSlug, bundleId, onChange, settings]);

  const handleEditorSaved = useCallback((buffer: ArrayBuffer) => {
    setPendingBuffer(buffer);
    setPendingFormat('docx');
    onChange({ ...settings, title_page: { ...settings.title_page, edited: true } });
  }, [onChange, settings]);

  const flushTitlePage = useCallback(async () => {
    if (!pendingBuffer || !pendingFormat) return;

    // Get a pre-signed URL for direct Azure upload
    const sasRes = await backendClient.post(
      `/bundle/matter/${matterSlug}/${bundleId}/title-page/sas?format=${encodeURIComponent(pendingFormat)}`,
    );
    const { upload_url } = sasRes.data;

    // Upload directly to Azure Storage
    const mimeType = pendingFormat === 'docx'
      ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : 'application/pdf';
    const ext = pendingFormat === 'docx' ? '.docx' : '.pdf';
    const file = new File([pendingBuffer], `title-page${ext}`, { type: mimeType });
    await uploadFileWithRetry(upload_url, file, file.name);

    setPendingBuffer(null);
    setPendingFormat(null);
  }, [matterSlug, bundleId, pendingBuffer, pendingFormat]);

  return {
    format,
    previewBuffer,
    previewLoading,
    resetting,
    handleUpload,
    handleReset,
    handleEditorSaved,
    flushTitlePage,
  };
}
